import { Typography, Stack, Box } from '@mui/material';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import LoadingButton from '@/@mantis/components/@extended/LoadingButton';
import { useGetAllContacts } from '@/api/endpoints/contact/useGetAllContacts';
import { useSendEmailByLocation } from '@/api/endpoints/conversation/useSendEmailByLocation';
import { Users01Icon } from '@/assets/icons/Users01Icon';
import { pluralize } from '@/utils/text';

import FormAutocomplete, { Option as BaseOption } from '../form/FormAutocomplete';
import FormEmailAttachments from '../form/FormEmailAttachments';
import FormTextField from '../form/FormTextField';
import FormWysiwygEditor from '../form/FormWysiwygEditor';
import ModalWithForm from '../modals/ModalWithForm';

interface Props {
  locationId: number;
  onClose: () => void;
  onEmailSent?: () => void;
  preSelectedContactEmail?: string;
}

interface Option extends BaseOption {
  contactId: number;
}

interface FormData {
  subject: string;
  body: string;
  recipients: Option[];
  attachments: FileList;
}

const EmailSendModal: React.FC<Props> = ({
  locationId,
  onClose,
  onEmailSent,
  preSelectedContactEmail,
}) => {
  const form = useForm<FormData>({
    defaultValues: {
      recipients: [],
    },
  });
  const {
    control,
    formState: { errors },
    register,
    setValue,
    watch,
  } = form;

  const recipients = watch('recipients');

  const getAllContactsQuery = useGetAllContacts();
  const sendEmailMutation = useSendEmailByLocation({
    onSuccess: () => {
      onEmailSent?.();
      onClose();
    },
  });

  const recipientsOptions: Option[] = useMemo(() => {
    if (!getAllContactsQuery.data) return [];

    return getAllContactsQuery.data
      .flatMap(contact =>
        contact.details
          .filter(detail => detail.type === 'email' && detail.value)
          .map(detail => ({
            label: `${contact.name} <${detail.value}>`,
            value: detail.value,
            contactId: contact.id,
          }))
      )
      .filter(
        (option, index, array) => array.findIndex(item => item.value === option.value) === index
      );
  }, [getAllContactsQuery.data]);

  // Pre-select contact when provided
  useEffect(() => {
    if (!preSelectedContactEmail || !recipientsOptions.length) return;

    const contactOption = recipientsOptions.find(
      option => option.value === preSelectedContactEmail
    );

    if (contactOption) {
      setValue('recipients', [contactOption]);
    }
  }, [preSelectedContactEmail, recipientsOptions, setValue]);

  const onSelectAll = useCallback(() => {
    setValue('recipients', recipientsOptions, { shouldDirty: true, shouldTouch: true });
  }, [recipientsOptions, setValue]);

  const onSubmit = useCallback(
    (data: FormData) =>
      sendEmailMutation.mutateAsync({
        subject: data.subject,
        body: data.body,
        recipients: data.recipients.map(recipient => recipient.value.toString()),
        attachments: data.attachments,
        locationId: Number(locationId),
      }),
    [sendEmailMutation, locationId]
  );

  return (
    <ModalWithForm
      onSubmit={onSubmit}
      onClose={onClose}
      form={form}
      maxWidth="md"
      title="Send Email"
      buttonLabel="Send"
      isDataLoading={getAllContactsQuery.isLoading}
      confirmActionProps={
        recipients.length > 1
          ? {
            children: (
              <>
                <Typography variant="body1">
                    Are you sure you want to send this email to the following {recipients.length}{' '}
                  {pluralize(recipients.length, 'recipient')}?
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {recipients.map(recipient => recipient.label).join(', ')}
                </Typography>
              </>
            ),
          }
          : undefined
      }
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <FormAutocomplete
          multiple
          label="Select Recipients"
          placeholder="Select Recipients"
          filterSelectedOptions
          required
          control={control}
          {...register('recipients', {
            validate: value => {
              if (value.length === 0) {
                return 'At least one recipient is required';
              }
              return true;
            },
          })}
          size="small"
          options={recipientsOptions}
          error={!!errors.recipients}
          helperText={errors.recipients?.message ?? 'Select recipients from contact list'}
        />

        <Box>
          <LoadingButton
            variant="outlined"
            color="secondary"
            startIcon={<Users01Icon />}
            onClick={onSelectAll}
            loading={getAllContactsQuery.isLoading}
            disabled={!getAllContactsQuery.data}
            size="small"
          >
            Select All
          </LoadingButton>
        </Box>
      </Stack>

      <FormTextField
        label="Subject"
        required
        control={control}
        {...register('subject', {
          required: 'Subject is required',
        })}
        placeholder="Enter Subject"
        size="small"
        error={!!errors.subject}
        helperText={errors.subject?.message}
      />

      <FormWysiwygEditor
        placeholder="Message…"
        required
        control={control}
        {...register('body', {
          required: 'Message is required',
        })}
        error={!!errors.body}
        helperText={errors.body?.message}
        size="small"
      />

      <FormEmailAttachments multiple size="small" control={control} {...register('attachments')} />
    </ModalWithForm>
  );
};

export default EmailSendModal;
